import { useTranslate } from '@tolgee/react';
import React from 'react';
import Tippy from '@tippyjs/react';
import { RefereeListItemFragment } from '@amf/shared/types/graphql';
import useRefereeStats from '@amf/shared/data/hooks/useRefereeStats';

interface RefereeStatsProps {
  referee: RefereeListItemFragment;
  title?: string;
}

function RefereeStats({ referee, title }: RefereeStatsProps) {
  const { t } = useTranslate();
  const { stats, summary } = useRefereeStats(referee.id);

  if (stats.length === 0) return null;
  return (
    <div className='PlayerStats'>
      {title && <h2 className='PlayerStats__title'>{title}</h2>}
      <div className='PlayerStats__content'>
        <table>
          <thead>
            <tr>
              <th className='left'>{t('referee.stats.season')}</th>
              <th className='left'>{t('referee.stats.league')}</th>
              <th>{t('referee.stats.matches')}</th>
              <th>{t('referee.stats.matchesAsMain')}</th>
              <th>{t('referee.stats.matchesAsAssist')}</th>
              <th>
                {t('referee.stats.yellowCards')}
                <Tippy
                  content={t('referee.stats.mainRefereeOnly')}
                  animation='shift-away'
                  theme='light'
                >
                  <span>
                    <i className='bi bi-info-circle-fill' />
                  </span>
                </Tippy>
              </th>
              <th>
                {t('referee.stats.redCards')}
                <Tippy
                  content={t('referee.stats.mainRefereeOnly')}
                  animation='shift-away'
                  theme='light'
                >
                  <span>
                    <i className='bi bi-info-circle-fill' />
                  </span>
                </Tippy>
              </th>
            </tr>
          </thead>
          <tbody>
            {stats &&
              stats.map(item => (
                <tr key={item.id}>
                  <td className='left'>
                    <strong>{item.season.name}</strong>
                  </td>
                  <td className='left PlayerStats__league'>
                    <strong>{item.leagueName}</strong>
                  </td>
                  <td>{item.matches}</td>
                  <td>{item.matchesAsMain}</td>
                  <td>{item.matchesAsAssist}</td>
                  <td>
                    {item.yellowCards}{' '}
                    {item.matches > 0 && (
                      <Tippy
                        content={t('referee.stats.yellowCards.avg')}
                        animation='shift-away'
                        theme='light'
                      >
                        <small className='PlayerStats__avg'>
                          ({(item.yellowCards / item.matches).toFixed(1)})
                        </small>
                      </Tippy>
                    )}
                  </td>
                  <td>
                    {item.redCards}{' '}
                    {item.matches > 0 && (
                      <Tippy
                        content={t('referee.stats.redCards.avg')}
                        animation='shift-away'
                        theme='light'
                      >
                        <small className='PlayerStats__avg'>
                          ({(item.redCards / item.matches).toFixed(1)})
                        </small>
                      </Tippy>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
          {summary && (
            <tfoot>
              <tr key={summary.id}>
                <th className='left' colSpan={2}>
                  <strong>{t('referee.stats.summary')}</strong>
                </th>
                <th className='fs-normal'>{summary.matches}</th>
                <th className='fs-normal'>{summary.matchesAsMain}</th>
                <th className='fs-normal'>{summary.matchesAsAssist}</th>
                <th className='fs-normal'>
                  {summary.yellowCards}{' '}
                  {summary.matches > 0 && (
                    <Tippy
                      content={t('referee.stats.yellowCards.avg')}
                      animation='shift-away'
                      theme='light'
                    >
                      <small className='PlayerStats__avg'>
                        ({(summary.yellowCards / summary.matches).toFixed(1)})
                      </small>
                    </Tippy>
                  )}
                </th>
                <th className='fs-normal'>
                  {summary.redCards}{' '}
                  {summary.matches > 0 && (
                    <Tippy
                      content={t('referee.stats.redCards.avg')}
                      animation='shift-away'
                      theme='light'
                    >
                      <small className='PlayerStats__avg'>
                        ({(summary.redCards / summary.matches).toFixed(1)})
                      </small>
                    </Tippy>
                  )}
                </th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default RefereeStats;
